// Core data models for frontend/backend compatibility

// Import and re-export filter types for frontend consumption
import type { NormalizedFilterSet, FilterStageCondition, FilterStage, MatchCriteria } from './filters/NormalizedFilterSet.js';
import type { OPSyncChunkResults } from './types/op-sync-types.js';
import { isNormalizedFilterSet, validateNormalizedFilterSet, isValidFilterStage } from './filters/NormalizedFilterSet.js';
export type { NormalizedFilterSet, FilterStageCondition, FilterStage, MatchCriteria };
export { isNormalizedFilterSet, validateNormalizedFilterSet, isValidFilterStage };

// Base PartitionKey/RowKey compatibility entity
export interface ITableEntity {
  partitionKey?: string;
  rowKey?: string;
  timestamp?: Date;
  etag?: string;
}

// Common log fields
export interface BaseLogEntity extends ITableEntity {
  timestampStr: string;       // string representation of timestamp
  createdOn?: string;
  lastUpdated?: string;
}

// Legacy field-pair interface for backward compatibility
export interface FieldPair {
  parentField: string;
  childField: string;
}

// Enhanced field mapping with template support
export interface EnhancedFieldMapping {
  targetField: string;                    // The child field to update
  mappingType: 'direct' | 'template' | 'static';

  // For direct mapping (backward compatibility)
  sourceField?: string;                   // Parent field to copy from

  // For template mapping
  template?: string;                      // Template string like "{parent.pl_name} - {child.status}"
  templateFields?: TemplateField[];       // Fields referenced in the template

  // For static value
  staticValue?: string;                   // Static value to set

  // Transformation support
  transformation?: {
    type: 'formula' | 'lookup' | 'choice';
    // For formula transformations
    expression?: string;
    // For lookup transformations
    sourceEntity?: string;
    targetEntity?: string;
    matchField?: string;
    // For choice/picklist mappings
    mappings?: Record<string, string>;
  };

  // Source modifiers (e.g., multiply, divide)
  sourceModifier?: {
    type: 'multiply' | 'divide' | 'add' | 'subtract';
    value: number;
  };

  // Clone/convert review flag
  requiresReview?: boolean;               // Whether this mapping needs manual review after conversion
  reviewReason?: string;                  // Why review is needed (e.g., "Formula cannot be reversed")

  // Legacy support - will be converted to appropriate mapping type
  parentField?: string;                   // For backward compatibility with FieldPair
  childField?: string;                    // For backward compatibility with FieldPair
  isRequired?: boolean;                   // Whether this field mapping is required
}

// Template field reference
export interface TemplateField {
  field: string;                          // Field name (e.g., "pl_name")
  source: 'parent' | 'child';             // Which entity the field belongs to
  placeholder: string;                    // Placeholder in template (e.g., "{parent.pl_name}")
}

// Formula parameter for complex template expressions
export interface FormulaParameter {
  type: 'field' | 'text' | 'formula';
  value: string;
  source?: 'parent' | 'child';
  label?: string; // For display purposes
}

// Template segment for building complex templates
export interface TemplateSegment {
  id: string;
  type: 'field' | 'text' | 'formula';
  source?: 'parent' | 'child';
  field?: string;
  text?: string;
  // For formula type
  function?: string;
  parameters?: FormulaParameter[];
}

// Unified field mapping for three-processor architecture
export interface UnifiedFieldMapping {
  mappingType: 'direct' | 'template' | 'static' | 'formula';
  sourceField?: string;      // Named dynamically based on context
  targetField: string;        // Named dynamically based on context

  // Template configuration
  template?: string;
  templateFields?: Array<{
    field: string;
    source: 'source' | 'target';  // Generic naming
    placeholder: string;
  }>;

  // Formula configuration
  formula?: {
    function: string;
    parameters: FormulaParameter[];
  };

  // Static value
  staticValue?: any;

  // Optional transformation
  transform?: string;
}

// Mapping between parent and child entities
export interface EntityMapping extends ITableEntity {
  name: string;
  parentEntity: string;
  childEntity: string;
  parentLookupField: string;
  childLookupField: string;
  direction: 'childToParent' | 'parentToChild';
  fieldMappings: FieldPair[]; // Legacy field mappings for backward compatibility
  enhancedFieldMappings?: EnhancedFieldMapping[]; // New template-based field mappings
  lastUpdated: string;
  isValidated?: boolean;

  // Association support fields
  enableAssociation?: boolean;           // Whether to create Dataverse associations
  navigationProperty?: string;           // Navigation property name (e.g., "contact_customer_accounts")

  // Advanced filters configuration
  filters?: any[];                       // Filter configurations for advanced filtering

  // V2 compatibility fields for direct sync mappings
  fromFilters?: any[];                   // Filters for source entity (added for V2 compatibility)
  toFilters?: any[];                     // Filters for target entity (added for V2 compatibility)
  reverseSyncFieldMappings?: UnifiedFieldMapping[]; // For reverse sync mode compatibility
}

// Entity mapping mode enumeration for V2
export enum EntityMappingMode {
  CreateAssociation = 'create_association',  // Type 1: Create Dataverse associations
  SyncFields = 'sync_fields',                // Type 2: Sync fields between associated records (parent → children)
  ReverseSyncFields = 'reverse_sync_fields'  // Type 3: Reverse sync fields (child → parent)
}

// Version 2 of Entity Mapping with improved from/to model
export interface EntityMappingV2 extends ITableEntity {
  // Core identification
  mappingId: string;
  name: string;
  mode: EntityMappingMode;
  version: 2;

  // From/To structure (replaces parent/child)
  fromEntity: string;                    // Source entity
  toEntity: string;                      // Destination entity
  navigationProperty: string;            // Required navigation property

  // Entity metadata for proper OData operations
  fromEntitySetName?: string;            // Pluralized entity set name for 'from' entity (e.g., "pl_titles")
  toEntitySetName?: string;              // Pluralized entity set name for 'to' entity (e.g., "pl_products")

  // Filters for each direction
  fromFilters?: any[];                   // Filter which 'from' records to process (legacy, will be deprecated)
  toFilters?: any[];                     // Filter which 'to' records to update (legacy, will be deprecated)

  /**
   * Normalized filter structure (Phase 1+)
   *
   * Replaces fromFilters/toFilters with a clean, mode-aware structure.
   * During dual-write period (Phases 1-3), both formats are maintained.
   * After Phase 4, this becomes the primary filter storage.
   *
   * Storage: JSON-serialized NormalizedFilterSet
   * Runtime: Parsed to NormalizedFilterSet object for use in code
   *
   * @see docs/FILTER_STABILIZATION_RECOMMENDATIONS.md
   * @see api/src/lib/filters/NormalizedFilterSet.ts
   */
  normalizedFilters?: NormalizedFilterSet | string;  // Runtime: object, Storage: JSON string

  // Mode-specific fields
  // For CreateAssociation mode:
  fromLookupField?: string;              // Lookup field in 'from' entity
  toLookupField?: string;                // Lookup field in 'to' entity
  associationMethod?: 'lookup_update' | 'collection_add'; // How to create the association
  lookupEntity?: string;                 // Which entity owns the lookup field (for lookup_update method)
  lookupFieldName?: string;              // The actual field name to update (from referencingAttribute)
  skipExisting?: boolean;                // Skip records that are already associated

  // Auto-create target records feature (for CreateAssociation mode)
  autoCreateTarget?: boolean;            // Automatically create missing target records
  targetDefaults?: {                     // Default field values for auto-created records
    [fieldName: string]: string | number | boolean | null;
  };
  trackAutoCreated?: boolean;            // Track auto-created records count in progress
  autoCreatedMarkerField?: string;       // Boolean field to mark auto-created records

  // For SyncFields mode:
  fieldMappings?: EnhancedFieldMapping[]; // Field mappings from source to destination

  // For ReverseSyncFields mode:
  reverseSyncParentLookupField?: string; // Field on parent entity with lookup value
  reverseSyncChildMatchingField?: string; // Field on child entity to match against
  matchOperator?: 'equals' | 'contains' | 'startswith' | 'endswith'; // How to match values
  disambiguationStrategy?: {             // Handle multiple matches
    orderBy?: string;                    // Field to order by
    direction?: 'asc' | 'desc';          // Sort direction
    takeFirst?: boolean;                 // Take first result after ordering
    additionalFilters?: any[];           // Additional filters to narrow results
  };
  reverseSyncFieldMappings?: UnifiedFieldMapping[]; // Field mappings (child → parent)

  // Modified date filtering
  modifiedDateFilter?: ModifiedDateFilter; // Optional date-based filtering for incremental syncs

  // Clone/Convert tracking metadata
  conversionMetadata?: {
    convertedFrom?: EntityMappingMode;     // Original mode before conversion
    convertedAt?: string;                  // ISO timestamp of conversion
    firstSyncCompleted?: boolean;          // Whether baseline sync has run (for reverse→direct conversions)
    sourceMappingId?: string;              // ID of the original mapping if cloned
  };

  // Metadata
  lastUpdated: string;
  isValidated?: boolean;

  // Legacy compatibility (deprecated, for migration purposes)
  /** @deprecated Use fromEntity/toEntity instead */
  direction?: 'childToParent' | 'parentToChild';
  /** @deprecated Use fromEntity instead */
  parentEntity?: string;
  /** @deprecated Use toEntity instead */
  childEntity?: string;
  /** @deprecated Use fromLookupField instead */
  parentLookupField?: string;
  /** @deprecated Use toLookupField instead */
  childLookupField?: string;
  /** @deprecated Use mode instead */
  enableAssociation?: boolean;
  /** @deprecated Use fromFilters/toFilters instead */
  filters?: any[];
}

/**
 * Modified date filtering configuration for Entity Mappings
 * Allows filtering records based on modification date to enable incremental syncs
 */
export interface ModifiedDateFilter {
  enabled: boolean;                      // Whether date filtering is active
  field: string;                         // Field to filter on (default: 'modifiedon')
  mode: 'absolute' | 'relative' | 'scheduler';

  // For absolute mode: specific date range
  absoluteRange?: {
    from?: string;                       // ISO date string (start date)
    to?: string;                         // ISO date string (end date)
  };

  // For relative mode: rolling window from now
  relativeRange?: {
    value: number;                       // Number of time units
    unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
  };

  // For scheduler mode: allows runtime override
  schedulerOverride?: boolean;           // Allow scheduler to provide runtime filter
}

// Union type for handling both versions during migration
export type EntityMappingAny = EntityMapping | EntityMappingV2;

// Type guard to check if mapping is V2
export function isEntityMappingV2(mapping: EntityMappingAny): mapping is EntityMappingV2 {
  return 'version' in mapping && mapping.version === 2;
}

// Supported data types for explicit values
export enum ExplicitValueDataType {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Date = 'date'
}

// Source type for field mappings
export enum MappingSourceType {
  Field = 'field',       // Map from a KUP field
  Explicit = 'explicit'  // Use an explicit static value
}

// Defines mapping between a source and target field
export interface FieldMapping extends ITableEntity {
  entityName: string;
  kupField: string;
  hsField: string;
  isChoiceField: boolean;
  syncMode: SyncMode;
  mappingType?: string;
  ruleKey?: string;
  
  // New fields for explicit value support
  sourceType?: MappingSourceType;        // Defaults to 'field' for backward compatibility
  explicitValue?: string | number | boolean | null;  // Static value when sourceType is 'explicit'
  dataType?: ExplicitValueDataType;      // Data type for explicit values
}

// Choice/option set value mapping
export interface ChoiceMapping extends ITableEntity {
  entityName: string;
  fieldName: string;
  kupValue: string;
  hsValue: string;
  hsLabel?: string;
}

// Task execution record for tracking individual scheduled executions
export interface TaskExecutionRecord {
  executionId: string;
  startTime: string; // ISO 8601 date string
  endTime?: string;  // ISO 8601 date string
  status: 'succeeded' | 'failed' | 'running' | 'retrying' | 'skipped';
  output?: any; // or string for logs/output
  error?: string;
}

// Standalone task execution entity stored in separate partition for efficient querying
// Partition Key: TaskExecution_{date} (e.g., TaskExecution_2025-10-01)
// Row Key: {timestamp}_{taskId}_{executionId} (e.g., 133000123_sched_xxx_exec_xxx)
export interface TaskExecutionEntity extends ITableEntity {
  // Identifiers
  taskId: string;                    // ID of the scheduled task
  taskName?: string;                 // Name for easy identification
  executionId: string;               // Unique execution ID
  parentExecutionId?: string;        // Links child execution to parent execution

  // Timing
  startTime: string;                 // ISO 8601 date string
  endTime?: string;                  // ISO 8601 date string
  durationMs?: number;               // Calculated duration in milliseconds

  // Status and results
  status: 'succeeded' | 'failed' | 'running' | 'retrying' | 'skipped' | 'stopped' | 'cancelled';
  recordsFound?: number;             // Total records found by query
  recordsProcessed?: number;         // Records actually processed
  recordsSuccessful?: number;        // Successfully updated
  recordsFailed?: number;            // Failed to process
  recordsSkipped?: number;           // Skipped (no update needed)

  // Links and metadata
  batchId?: string;                  // Link to the batch job entity
  jobId?: string;                    // Link to the job entity
  jobType?: string;                  // Registered job type identifier
  entityMappingId?: string;          // Entity mapping used
  entityMappingName?: string;        // Entity mapping name
  error?: string;                    // Error message if failed
  output?: string;                   // JSON string of output data
}

// Schedule configuration for recurring task execution
export interface ScheduleConfiguration {
  enabled: boolean;
  cronExpression?: string;
  interval?: {
    value: number;
    unit: 'minutes' | 'hours' | 'days' | 'weeks';
  };
  startDate?: string; // ISO 8601 date string
  endDate?: string;   // ISO 8601 date string
  timezone: string;   // e.g., 'Europe/London', 'America/New_York'
  maxRetries: number;
  retryDelayMinutes: number;
}

// Long-running management tasks with scheduling capabilities
export interface ManagementTask extends ITableEntity {
  // Core task properties
  id: string;
  name: string;
  description: string;
  taskType: string; // e.g., 'dataSync', 'reportGeneration'
  parameters: Record<string, any>;

  // Legacy fields (maintaining backward compatibility)
  status?: TaskStatus;
  entityMapping?: string;
  entityMappingName?: string;
  payload?: string;
  metadata?: string;
  response?: string;
  recordLimit?: number;
  totalRecords?: number;
  // Data retrieval tracking (Stage 1)
  retrievedRecords?: number;      // Count of records retrieved and queued
  retrievedPercentage?: number;   // Percentage of records retrieved
  // Processing tracking (Stage 2)
  processedRecords?: number;      // Count of records actually processed
  processedPercentage?: number;   // Percentage of records processed
  createdOn: string;
  completedOn?: string;
  owner?: string;
  results?: string;
  excludeEmptyParentLookup?: boolean;

  // Enhanced completion tracking (prevents premature completion)
  successfulRecords?: number;    // Count of successfully processed records
  failedRecords?: number;        // Count of failed records
  skippedRecords?: number;       // Count of skipped records (no update needed, no child found, etc.)
  isCompleted?: boolean;         // Explicit completion flag (prevents auto-completion)

  // New scheduling fields
  schedule?: ScheduleConfiguration;
  lastExecutionTime?: string;    // ISO 8601 date string
  nextExecutionTime?: string;    // ISO 8601 date string
  // executionHistory removed - use taskExecutionService to query execution records by taskId

  // Job orchestration fields
  jobType?: string;              // e.g., 'Dataverse.EntityMapping', 'SharePoint.ImageResolution'
  jobId?: string;                // Link to entry in Jobs table (rowKey) when available

  // 🔑 Links to scheduled task and execution (queryable columns)
  scheduledTaskId?: string;    // Link to parent scheduled task definition (if triggered by scheduler)
  executionId?: string;         // Link to TaskExecutionEntity for this specific run

  // Parent/child task hierarchy
  parentTaskId?: string;        // NULL = parent/standalone, set for children
  executionOrder?: number;      // Order within parent
  onChildFailure?: 'stop' | 'skip' | 'retry';
  childCount?: number;          // Number of children (for parents)

  // Additional metadata
  taskGroup?: string;
  tags?: string[];
  isEnabled?: boolean;
  version?: number;

  // Claim/lock state (lets the UI surface a "Reset stuck task" affordance)
  claimedBy?: string;
  claimedAt?: string;
}

// Field-level synchronization log
export interface FieldMappingSyncLog extends BaseLogEntity {
  entityName: string;
  entityId: string;
  batchId: string;
  updatedFields?: string;
  processed: boolean;
  processedAt: string;
  syncLogs?: string;
  status: SyncStatus;
  payload?: string;
  message?: string;
  error?: string;
  errorMessage?: string;
  mappingId?: string;
  relationName?: string;
  correlationId?: string;
}

export interface FieldMappingPerformanceData {
  entityName: string;
  entityId: string;
  itemCount: number;
  durationMs: number;
  success: boolean;
}

// Entity-level synchronization log
export interface EntityMappingSyncLog extends BaseLogEntity {
  parentEntity: string;
  parentId: string;
  childEntity: string;
  childId: string;
  success: boolean;
  syncStatus: SyncStatus;
  message?: string;
  updatedFields?: string;
  error?: string;
  batchId?: string;
  mappingId: string;
  processingLog?: string;
  processed: boolean;
  syncLogs?: string;
  processedAt?: string;
  dataverseUpdateSuccess?: boolean;
  primaryEntityId?: string;
  messageName?: string;
  stage?: number;
  retryCount?: number;
  userId?: string;
  payload?: string;
  relationName?: string;
  // Enhanced fields for detailed sync analysis
  comparisonDetails?: string; // JSON string containing field-by-field comparison results
  skippedReasons?: string; // JSON array of reasons why fields were skipped
  associationStatus?: string; // JSON object with association operation details
  filterResults?: string; // JSON object with information about applied filters
  lookupFieldValues?: string; // JSON object with actual lookup field values
  parentLookupField?: string; // The lookup field used in parent entity
  childLookupField?: string; // The lookup field used in child entity
  parentLookupValue?: string; // The actual value of parent lookup field
  childLookupValue?: string; // The actual value of child lookup field
  mappingConfiguration?: string; // JSON object with the mapping rules used
  // Query diagnostics for debugging failed syncs
  queryDetails?: string; // JSON object with the full query details executed
  filterApplied?: string; // The complete filter string that was applied
  diagnosticQueries?: string; // JSON object with results of diagnostic queries when child not found
  potentialMatches?: number; // Count of records that match lookup field alone (without additional filters)
  // Task Type & Execution Tracking
  taskType?: 'manual' | 'scheduled';     // Type of triggering task
  scheduledTaskId?: string;              // Link to scheduled task definition (if triggered by scheduler)
  executionId?: string;                  // Link to specific TaskExecutionEntity
}

// Enums for task and sync statuses and operations
export enum TaskStatus {
  Pending = 'pending',
  Started = 'started',
  InProgress = 'running',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled',
  // New scheduling statuses
  Idle = 'idle',
  Scheduled = 'scheduled',
  Disabled = 'disabled'
}

export enum SyncStatus {
  Pending = 'pending',
  Done = 'done',
  NoUpdateNeeded = 'no_update_needed',
  Error = 'error',
  NoChange = 'no_change',
  Skipped = 'skipped',
  NoChildFound = 'no_child_found',
  NoParentFound = 'no_parent_found',
}

export enum SyncMode {
  Always = 'always',
  IfEmpty = 'ifEmpty',
  Ignore = 'ignore',
  Custom = 'custom'
}

export enum SyncOperation {
  Unknown = 0,
  Create = 1,
  Update = 2,
  Delete = 3
}

// Mapping task types and queue interfaces (migrated from queueTypes/mappingTaskTypes.ts)

/**
 * Type of mapping task being performed
 * Used as a discriminator field to route messages to the appropriate processor
 */
export enum MappingTaskType {
  EntityMapping = 'EntityMapping',  // For parent-child entity relationship mapping
  FieldMapping = 'FieldMapping',    // For field-level synchronization (from processLogEntries)
  SqlSyncBatch = 'SqlSyncBatch',    // For SQL-to-Dataverse batch synchronization
  SqlSyncChangeDetection = 'SqlSyncChangeDetection',  // For SQL change detection (queue-based)
  SqlSyncChunk = 'SqlSyncChunk',    // For chunked parallel SQL-to-Dataverse sync
  FieldSyncChunk = 'FieldSyncChunk', // For chunked parallel field mapping sync
  SharePointImageDiscovery = 'SharePointImageDiscovery',  // SharePoint job discovery phase
  SharePointImageChunk = 'SharePointImageChunk',  // SharePoint image processing chunk
  SentimentAnalysis = 'SentimentAnalysis',  // Sentiment analysis for HubSpot emails
  OPSyncChangeDetection = 'OPSyncChangeDetection',  // OP Sync change detection (queue-based)
  OPSyncChunk = 'OPSyncChunk',  // OP Sync chunk processing (dry-run or live sync)
}

/**
 * Base interface for all mapping tasks
 * Contains common properties shared by all task types
 */
export interface BaseMappingTask {
  // Discriminator field to determine task type
  type: MappingTaskType;
  
  // Tracking and correlation
  id?: string;                    // Optional unique ID for the task
  timestamp?: string;             // When the task was created
  
  // Error handling and retries
  retryCount?: number;            // Number of times this task has been retried
  maxRetries?: number;            // Maximum number of retries allowed
  lastError?: string;             // Last error message if task failed
}

/**
 * Entity mapping task for parent-child relationships
 */
export interface EntityMappingTask extends BaseMappingTask {
  type: MappingTaskType.EntityMapping;

  // Task-specific properties
  batchId: string;                // ID of the batch this task belongs to
  mappingId: string;              // RowKey of the entity mapping definition
  parentId: string;               // ID of the parent entity to process
  parentEntity?: string;          // Optional name of the parent entity
  childEntity?: string;           // Optional name of the child entity
  correlationId?: string;         // Optional correlation ID for tracking
  environment?: string;           // Optional environment tag for the task
  functionName?: string;          // Optional function name for logging
  inputPayload?: string;          // Optional input payload for the task
  // Task Type & Execution Tracking
  taskType?: 'manual' | 'scheduled';     // Type of triggering task
  scheduledTaskId?: string;              // Link to scheduled task definition
  executionId?: string;                  // Link to specific execution
}

/**
 * Field mapping task for synchronizing fields from webhook events
 */
export interface FieldMappingTask extends BaseMappingTask {
  type: MappingTaskType.FieldMapping;

  // Task-specific properties
  entityName: string;             // Logical name of the entity
  entityId: string;
  batchId: string;             // ID of the entity record
  messageName: string;            // Message name from the webhook (e.g., "Update")
  stage?: string;                 // Processing stage (e.g., "Post")
  userId?: string;                // ID of the user who triggered the event
  attributes?: Record<string, any>;  // Fields/attributes to be updated
  // Task Type & Execution Tracking
  taskType?: 'manual' | 'scheduled';
  scheduledTaskId?: string;
  executionId?: string;
}

/**
 * SQL Sync batch task for SQL-to-Dataverse synchronization
 * Used for Series and Title entity syncs from Oracle replica
 *
 * Note: Environment is determined by SQL_CONNECTION_STRING env var, not per-task
 */
export interface SqlSyncBatchTask extends BaseMappingTask {
  type: MappingTaskType.SqlSyncBatch;

  // Task-specific properties
  jobId: string;                  // Unique job identifier (for progress tracking in Redis)
  entityType: string;             // 'Title' | 'Series' | 'Contract' | 'Product' | 'MainSeries'
  batchNumber: number;            // For progress tracking (1-based)
  batchSize: number;              // Records to fetch per batch
  maxRecords?: number;            // Optional cap for test runs
  correlationId?: string;         // For end-to-end tracing
}

/**
 * SQL Sync chunk task - processes a specific set of record IDs
 * Part of the chunked queue architecture for parallel processing
 *
 * Instead of a single long-running worker, jobs are split into chunks
 * of ~250 records each, queued as separate messages for parallel processing.
 */
export interface SqlSyncChunkTask extends BaseMappingTask {
  type: MappingTaskType.SqlSyncChunk;

  // Job identification
  jobId: string;                  // Parent job ID (for progress tracking)
  entityType: string;             // 'Title' | 'Series' | 'Contract' | 'Product' | 'MainSeries'

  // Chunk identification
  chunkIndex: number;             // 0-based chunk number
  totalChunks: number;            // Total chunks in this job
  entityKeys: string[];           // Specific EntityKeys to process (max ~250)

  // Tracing
  correlationId?: string;         // For end-to-end tracing
}

/**
 * Field Sync chunk task - processes field mappings for a specific set of records
 * Part of the chunked queue architecture for parallel processing
 *
 * Instead of a single long-running HTTP request, jobs are split into chunks
 * of ~30 records each, queued as separate messages for parallel processing.
 */
export interface FieldSyncChunkTask extends BaseMappingTask {
  type: MappingTaskType.FieldSyncChunk;

  // Job identification
  jobId: string;                  // Parent job ID (for progress tracking)
  entityType: string;             // 'Title' | 'Series' | 'Contract' | 'Product' | 'MainSeries'

  // Chunk identification
  chunkIndex: number;             // 0-based chunk number
  totalChunks: number;            // Total chunks in this job
  entityKeys: string[];           // Specific EntityKeys to process (~30 per chunk)

  // Tracing
  correlationId?: string;         // For end-to-end tracing
}

/**
 * SQL Sync change detection task
 * Runs the stored procedure to detect changes and then triggers dispatch
 * This enables queue-based change detection that won't timeout for large tables
 *
 * Note: Environment is determined by SQL_CONNECTION_STRING env var, not per-task
 */
export interface SqlSyncChangeDetectionTask extends BaseMappingTask {
  type: MappingTaskType.SqlSyncChangeDetection;

  // Task-specific properties
  entityType: string;             // 'Title' | 'Series' | 'Contract' | 'Product' | 'MainSeries'
  scheduledTaskId?: string;       // Link to scheduled task (if triggered by scheduler)
  executionId?: string;           // Link to TaskExecutionEntity
  correlationId?: string;         // For end-to-end tracing

  // Reset mode: If true, truncate staging tables before detecting changes (full rescan)
  reset?: boolean;

  // Reset failed: If true, reset failed records to pending before detecting changes
  resetFailed?: boolean;

  // Auto-start: If true (default), automatically start sync after detecting changes
  autoStartSync?: boolean;

  // Paged Prices detection: continue scanning from this PRD_PRICE.PRICE_ID.
  // Only used by the Prices entity while it is too large for full-view scans.
  startPriceId?: number;
  batchSize?: number;
  pageNumber?: number;
  cumulativeNewRecords?: number;
  cumulativeUpdatedRecords?: number;
  cumulativeDeletedRecords?: number;
  cumulativeDurationMs?: number;
}

/**
 * SharePoint Image Discovery task
 * Stage 1: Scans configured sites and queues items for processing
 */
export interface SharePointImageDiscoveryTask extends BaseMappingTask {
  type: MappingTaskType.SharePointImageDiscovery;

  // Job identification
  jobId: string;                  // Unique job run ID (UUID)
  sharePointJobId: string;        // Reference to config.SharePointJobs

  // Execution context
  executionId?: string;           // Link to TaskExecutionEntity
  correlationId?: string;         // For end-to-end tracing
}

/**
 * SharePoint Image Chunk task
 * Stage 2: Processes a batch of images (download, extract metadata, update SharePoint)
 * Each chunk contains ~25 items to ensure completion within timeout
 */
export interface SharePointImageChunkTask extends BaseMappingTask {
  type: MappingTaskType.SharePointImageChunk;

  // Job identification
  jobId: string;                  // Parent job ID (for progress tracking)
  sharePointJobId: string;        // Reference to config.SharePointJobs

  // Site context
  siteIndex: number;              // Index into job's sites array
  siteId: string;                 // SharePoint site ID
  driveId: string;                // SharePoint drive ID

  // Chunk identification
  chunkIndex: number;             // 0-based chunk number for this site
  totalChunks: number;            // Total chunks for this site

  // Items to process (minimal info - full details fetched during processing)
  items: SharePointChunkItem[];

  // Processing configuration
  columns: SharePointColumnConfig;
  qualityConfig?: SharePointQualityConfig;
  dryRun: boolean;
  folderPath?: string;

  // Tracing
  correlationId?: string;         // For end-to-end tracing
}

/**
 * Minimal item info stored in queue message
 * Full file content is fetched during processing
 */
export interface SharePointChunkItem {
  id: string;                     // DriveItem ID
  name: string;                   // File name
  webUrl: string;                 // Web URL for logging/debugging
  size?: number;                  // File size in bytes (optional)
  path?: string;                  // Relative path (folder/subfolder/file.ext) if available
}

/**
 * Column configuration for SharePoint image processing
 */
export interface SharePointColumnConfig {
  width: string;
  height: string;
  dimensions?: string;
  dpiX: string;
  dpiY: string;
  fileSize: string;
  colorMode: string;
  processedDate: string;
  processingStatus: string;
  qualityStatus?: string;
  qualityScore?: string;
  qualityFlags?: string;
  recommendedAction?: string;
}

/**
 * Quality validation configuration for SharePoint images
 */
export interface SharePointQualityConfig {
  minDPI: number;
  preferredDPI: number;
  expectedColorMode?: 'RGB' | 'Grayscale' | 'CMYK';
  minWidth?: number;
  minHeight?: number;
  maxFileSize?: number;
}

/**
 * OP Sync change detection task
 * Triggers change detection from Dataverse to staging table,
 * optionally auto-dispatches dry-run or live sync
 */
export interface OPSyncChangeDetectionTask extends BaseMappingTask {
  type: MappingTaskType.OPSyncChangeDetection;

  entityType: string;
  sinceDate?: string;         // Optional lower bound for Dataverse modifiedon filtering
  dryRun?: boolean;           // Default true - run dry-run after detection
  autoDispatch?: boolean;     // Default true - auto-dispatch after detection
  maxRecords?: number;        // Optional cap for test runs
  auditRunId?: string;
  triggerSource?: 'manual' | 'scheduler';
  correlationId?: string;
}

/**
 * OP Sync chunk task - processes a specific set of records
 * for either dry-run (audit) or live sync mode
 */
export interface OPSyncChunkTask extends BaseMappingTask {
  type: MappingTaskType.OPSyncChunk;

  jobId: string;
  entityType: string;
  chunkIndex: number;
  totalChunks: number;
  entityKeys: string[];
  dryRun: boolean;
  auditRunId?: string;
  triggerSource?: 'manual' | 'scheduler';
  correlationId?: string;
  deferredRetry?: boolean;
  retryReason?: string;
  deferredBaseResults?: OPSyncChunkResults;
}

/**
 * Data retrieval state for tracking paginated Dataverse requests
 */
export interface DataRetrievalState {
  batchId: string;                // ID of the retrieval batch

  // For legacy jobs (entityMapping) or generic jobs (entityName/primaryIdField/filter)
  mappingTaskType: MappingTaskType;     // Type of mapping (copy, cleansing, transformation)
  entityMapping?: string;         // RowKey of the entity mapping being processed (optional for generic jobs)
  entityName?: string;            // Logical name of the entity (for generic filtered jobs)
  primaryIdField?: string;        // Primary ID field of the entity (for generic filtered jobs)
  filterObject?: any;             // The validated filter object (JSON)
  odataFilter?: string;           // The OData filter string used for Dataverse queries

  // Correlation tracking
  correlationId?: string;         // For end-to-end tracking across scheduler and queue
  scheduledTaskId?: string;       // Link to scheduled task if triggered by scheduler

  // 🔑 Task Type & Execution Tracking
  taskType?: 'manual' | 'scheduled';
  executionId?: string;

  startTime: string;              // When the retrieval started
  lastUpdated: string;            // When the state was last updated

  // Pagination tracking
  nextLink?: string;              // OData nextLink for continuation
  pageCount: number;              // Number of pages retrieved so far
  recordCount: number;            // Number of records retrieved so far

  // Status tracking
  status: DataRetrievalStatus;    // Current status of the retrieval
  errorCount: number;             // Number of errors encountered
  lastError?: string;             // Last error message

  // Completion
  isComplete: boolean;            // Whether retrieval is complete
  completedOn?: string;           // When retrieval completed

  // Deprecated: legacy OData filter (use odataFilter for generic jobs)
  filter?: string;
}

/**
 * Status of a data retrieval operation
 */
export enum DataRetrievalStatus {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Paused = 'paused',
  Completed = 'completed',
  Failed = 'failed',
  TimedOut = 'timed_out'
}

export enum MappingType { 
  Copy = 'copy',
  Cleansing = 'cleansing',
  Transformation = 'transformation'
}

export interface AttributeKVP {
    Key: string;
    Value: unknown;
}

export interface EntityImage {
    Key: string;
    Value: unknown;
}

export interface TargetEntity {
    LogicalName?: string;
    Id?: string;
    Attributes?: AttributeKVP[];
}

export interface RequestData {
    PrimaryEntityId?: string;
    PrimaryEntityName?: string;
    MessageName?: string;
    Stage?: string;
    UserId?: string;
    InputParameters?: {
        Target?: TargetEntity;
    };
    PostEntityImages?: EntityImage[];
    // Adding these properties to support alternative payload structures
    Target?: TargetEntity;
    entity?: TargetEntity;
    // Add LogicalName for direct entity format support
    LogicalName?: string;
    Id?: string;
}

export interface AttributesMap {
    [key: string]: unknown;
}

export interface SyncModeStat {
  mode: SyncMode;
  modeDisplay: string;
  count: number;
}

export interface SyncModeStatistics {
  entity: string;
  stats: SyncModeStat[];
}

export interface CreateFieldMappingRequest {
  type: "field";
  entityName: string;
  kupField: string;
  hsField: string;
  isChoiceField: boolean;
  syncMode: SyncMode;
  
  // New fields for explicit value support
  sourceType?: MappingSourceType;        // Defaults to 'field' for backward compatibility
  explicitValue?: string | number | boolean | null;  // Static value when sourceType is 'explicit'
  dataType?: ExplicitValueDataType;      // Data type for explicit values
}

export interface CreateChoiceMappingRequest {
  type: "choice";
  entityName: string;
  fieldName: string;
  kupValue: string;
  hsValue: string;
  hsLabel?: string;
}

export interface DiscoverMappingsRequest {
  action: "discover";
  entities: string[];
  syncMode: SyncMode;
}

export interface ApplyDiscoveryRequest {
  action: "applyDiscovery";
  fieldMappings: FieldMapping[];
  choiceMappings: ChoiceMapping[];
}

export interface UpdateSyncModeRequest {
  action: "updateSyncMode";
  entityName: string;
  kupFields: string[];
  syncMode: SyncMode;
}

export interface ApiResponse {
  success: boolean;
  message?: string;
}

export interface DiscoveryResponse extends ApiResponse {
  mode?: "preview" | "apply";
  fieldMappingsCreated?: number;
  choiceMappingsCreated?: number;
  proposedFieldMappings?: FieldMapping[];
  proposedChoiceMappings?: ChoiceMapping[];
  skipped?: number;
  logs?: string[];
  defaultSyncMode?: string;
}

export interface UpdateSyncModeResponse extends ApiResponse {
  updatedCount?: number;
  syncMode?: SyncMode;
  syncModeDisplay?: string;
}

export interface AllMappingsResponse {
  fieldMappings: FieldMapping[];
  choiceMappings: ChoiceMapping[];
}
export interface SyncLogsResponse {
  logs: FieldMappingSyncLog[];
  statusCounts?: {
    done: number;
    error: number;
    no_update_needed: number;
    pending: number;
    total: number;
  };
  totalCount?: number;
}


export interface CreateEntityMappingRequest {
  parentEntity: string;
  childEntity: string;
  childLookupField: string;
  parentLookupField: string;
  fieldMappings: FieldPair[];

  // Association support fields
  enableAssociation?: boolean;
  navigationProperty?: string;
}

export interface DataverseEntityInfo {
  MetadataId?: string;
  LogicalName?: string; 
  SchemaName?: string;
  ObjectTypeCode?: number;
  // Additional properties for fallback/mock data
  logicalName?: string;
  displayName?: string;
  objectTypeCode?: number;
}

export interface UpdateEntityMappingRequest {
  parentEntity: string;
  childEntity: string;
  childLookupField: string;
  parentLookupField: string;
  fieldMappings: FieldPair[];

  // Association support fields
  enableAssociation?: boolean;
  navigationProperty?: string;
}

export interface EntityMappingResponse {
  success: boolean;
  action?: 'created' | 'updated';
  message?: string;
  updatedCount?: number;
  metadata?: {
    entityUrl?: string;
    validatedWithDataverse?: boolean;
  };
}

export interface LocalizedLabel {
  Label: string;
  LanguageCode: number;
  IsManaged: boolean;
  MetadataId: string;
  HasChanged: boolean | null;
}


export interface DisplayNameObject {
  LocalizedLabels: LocalizedLabel[];
  UserLocalizedLabel: LocalizedLabel;
}

export interface DataverseFieldInfo {
  // Original field properties
  displayName?: string;
  logicalName: string;
  type?: string;
  isRequired?: boolean;
  isPrimaryKey?: boolean;
  isLookup?: boolean;
  
  // Properties from Dataverse API response
  MetadataId?: string;
  AttributeType?: string;
  IsPrimaryId?: boolean;
  IsValidForRead?: boolean;
  LogicalName?: string;
  SchemaName?: string;
  DisplayName?: DisplayNameObject;
  Description?: DisplayNameObject;
}

export interface TaskResponse {
  success: boolean;
  message: string;
  taskId?: string;
  batchId?: string;
}

export interface ProcessEntityRecordsParams {
  recordLimit?: number;
  parentEntity?: string;
  parentId?: string;
  entityMappingId?: string;
}

export interface BatchProcessResponse {
  batchId: string;
  status: string;
}

export interface TaskLogEntry {
  partitionKey: string;
  rowKey: string;
  timestamp: Date;
  name: string;
  status: "pending" | "running" | "completed" | "error";
  payload?: string;
  response?: string;
  recordLimit?: number;
  totalRecords?: number;
  // Data retrieval tracking (Stage 1)
  retrievedRecords?: number;
  retrievedPercentage?: number;
  // Processing tracking (Stage 2)
  processedRecords?: number;
  processedPercentage?: number;
  createdOn?: string;
  completedOn?: string;
  owner?: string | null;
  details?: string;
  errorMessage?: string;
  progress?: number;
  results?: string;
  // Entity mapping information
  entityMappingName?: string;
  // Task type (manual vs scheduled)
  taskType?: string;
}


export interface TaskLogsResponse {
  success: boolean;
  logs: TaskLogEntry[];
  statusCounts: {
    pending: number;
    running: number;
    completed: number;
    failed: number;
    total: number;
  };
  message?: string;
  error?: string;
}
export interface TableLogsQueryResponse {
  success: boolean;
  logs: TaskLogEntry[];
  statusCounts: {
    pending: number;
    running: number;
    completed: number;
    failed: number;
    total: number;
  };
  message?: string;
  error?: string;
}

// Client API function to match the server implementation
export interface QueryTableLogsParams {
  tableName: string;
  partitionKey?: string;
  rowKeyPrefix?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  batchId?: string;
  entity?: string;
  limit?: number;
  page?: number;
  jsonFilters?: { [key: string]: string | number | boolean };
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
  noCache?: boolean;
}

export interface ProcessingTaskMessage {
  action: 'startDataRetrieval' | 'continueDataRetrieval' | 'startFilteredDataRetrieval' | 'processSingleEntity';
  batchId: string;
  entityMapping?: string;
  recordLimit?: number;
  nextLink?: string;
  excludeEmptyParentLookup?: boolean;
  // For filtered jobs
  entity?: string;
  filterObject?: any;
  odataFilter?: string;
  // For single entity processing
  parentId?: string;
  correlationId?: string;
  environment?: string;
  // Task Type & Execution Tracking
  triggeredBy?: 'scheduler' | 'manual';
  scheduledTaskId?: string;
  executionId?: string;
}

export interface TaskProgressResponse {
  batchId: string;
  processed: number;
  total: number;
  percentage: number;
}

// Define interfaces for the log data
export interface EntitySyncLogData {
  name?: string;
  parentEntity: string;
  parentId: string;
  childEntity: string;
  childId: string;
  timestamp?: Date;
  mappingId: string;
  success: boolean;
  syncStatus: SyncStatus; 
  message?: string;
  updatedFields?: Record<string, any>;
  error?: string;
  batchId?: string;
  processingLog?: Record<string, any>;
  correlationId?: string;
  environment?: string;
  durationMs?: number;
  inputPayload?: string;
  errorStack?: string;
  // Diagnostic fields for failed syncs
  queryDetails?: string;
  filterApplied?: string;
  diagnosticQueries?: string;
  potentialMatches?: number;
  // Enhanced detailed analysis fields
  comparisonDetails?: Record<string, any>;
  associationStatus?: Record<string, any>;
  parentLookupField?: string;
  childLookupField?: string;
  parentLookupValue?: string;
  childLookupValue?: string;
  mappingConfiguration?: Record<string, any>;
  // Task trace fields
  scheduledTaskId?: string;
  executionId?: string;
}

/**
 * Query filter interface for searching item update logs
 */
export interface FieldMappingSyncLogFilter {
  entityName?: string;
  entityId?: string;
  status?: "success" | "failure";
  startTime?: Date;
  endTime?: Date;
  userId?: string;
  limit?: number;
  skip?: number;
  correlationId?: string;
  batchId?: string; // New optional property for batch ID
  searchTerm?: string; // Optional server-side search filter
  lastSeenTimestamp?: Date; // For cursor-based pagination
  lastSeenId?: string; // For cursor-based pagination
}

/**
 * Query result interface for item update logs
 */
export interface FieldMappingSyncLogQueryResult {
  logs: FieldMappingSyncLog[];
  totalCount: number;
  pageSize: number;
  skip: number;
  filters?: FieldMappingSyncLogFilter; // Echo back the filters used for the query
}


/**
 * Query filter interface for searching 
 */
export interface EntityMappingSyncLogFilter {
  parentEntity?: string;
  childEntity?: string;
  parentId?: string;
  childId?: string;
  success?: boolean;
  syncStatus?: SyncStatus; // Legacy: single status filter (deprecated, use statusFilter)
  statusFilter?: {         // New: multi-status filter with include/exclude mode
    mode: 'include' | 'exclude';
    statuses: string[];    // Array of SyncStatus values
  };
  batchId?: string;
  mappingId?: string;
  searchTerm?: string; // Optional server-side search filter
  environment?: string; // Add environment filter
  startTime?: Date;
  endTime?: Date;
  skip?: number;
  limit?: number;
  lastSeenTimestamp?: Date; // For cursor-based pagination
  lastSeenId?: string; // For cursor-based pagination
  // Task Type & Execution Tracking
  taskType?: 'manual' | 'scheduled';
  scheduledTaskId?: string;
  executionId?: string;

} 

/**
 * Query result interface for 
 */
export interface EntityMappingSyncLogQueryResult {
  logs: EntitySyncLogData[];
  totalCount: number;
  pageSize: number;
  skip: number;
  statusCounts?: {
    done: number;
    error: number;
    no_update_needed: number;
    pending: number;
    total: number;
  };
  filters?: EntityMappingSyncLogFilter; // Echo back the filters used for the query
}


export interface FieldMappingPerformanceData {
  entityName: string;
  entityId: string;
  itemCount: number;
  durationMs: number;
  success: boolean;
}

export interface EntityMappingPerformanceData {
  parentEntity: string;
  childEntity: string;
  operation: string;
  itemCount: number;
  durationMs: number;
  success: boolean;
}


// export interface FieldMapping {
//   entityName: string;     // Logical name of the entity
//   kupField: string;       // KUP field name
//   hsField: string;        // HS field name
//   isChoiceField: boolean; // Whether this is an option set/choice field
//   syncMode: SyncMode;     // Sync behavior mode
//   mappingType?: string;   // 'Copy' or 'Cleansing'
//   ruleKey?: string;       // Key for custom transform routines
// }

// export interface ChoiceMapping {
//   entityName: string;     // Logical name of the entity
//   fieldName: string;      // Field name (HS version)
//   kupValue: string;       // KUP option value
//   hsValue: string;       // HS option value
// }


// Type definitions
export interface EntitySyncData {
  parentEntity: string;
  parentId: string;
  childEntity: string;
  childId: string;
  syncStatus: string;
  success: boolean;
  message?: string;
  mappingId?: string;
  batchId?: string;
  correlationId?: string;
}

export interface EntitySyncPerformanceData {
  parentEntity: string;
  childEntity: string;
  operation: string;
  itemCount: number;
  durationMs: number;
  success: boolean;
}

export interface OperationLog {
  field: string;
  action: 'copied' | 'matched' | 'inconsistent' | 'skipped';
  kupValue: any;
  hsValue: any;
  note: string | null;
}

/**
 * Result of a sync operation.
 */
export interface SyncResult {
  updatedAttributes: Record<string, any>;
  logs: OperationLog[];
  hasUpdates: boolean;
  hsFieldsUpdated: string[];
}

/**
 * Result of a Dataverse update operation.
 */
export interface DataverseUpdateResult {
  success: boolean;
  message: string;
  duration?: number;
  updatedFields?: string[];
  error?: any;
  statusCode?: number;
}

/**
 * Enum defining the scope of synchronization.
 */
export enum SyncScope {
  PayloadFieldsOnly = 'payloadFieldsOnly',  // Only sync fields present in the webhook payload
  AllMappedFields = 'allMappedFields'       // Sync all fields that have mappings for the entity
}

/**
 * Information about a Dataverse relationship between entities
 */
export interface RelationshipInfo {
  schemaName: string;           // e.g., "contact_customer_accounts"
  relationshipType: '1:N' | 'N:1' | 'N:N';
  referencedEntity: string;
  referencingEntity: string;
  referencedAttribute?: string; // For 1:N/N:1
  referencingAttribute?: string; // For 1:N/N:1
  navigationPropertyName?: string;
  intersectEntityName?: string; // For N:N relationships
  displayName?: string;          // User-friendly name
  isCustom: boolean;             // Custom vs native
  isManaged: boolean;
}

// User Management and Role-Based Access Control Types

/**
 * User role definitions for role-based access control
 */
export enum UserRole {
  Admin = 'Platform Administrator',
  PowerUser = 'Platform Power User',
  User = 'Platform User',
  Viewer = 'Platform Viewer',
  ServiceAccount = 'Service Account',
  RoyaltiesAdmin = 'Royalties Administrator',
  RoyaltiesUser = 'Royalties User',
  RoyaltiesAuthor = 'Royalties Author',
  Public = 'public'
}

/**
 * Permission definitions for different sections and operations
 */
export interface SectionPermissions {
  view: boolean;
  edit: boolean;
  execute?: boolean;
  export?: boolean;
}

/**
 * Complete permission structure for all application sections
 */
export interface UserPermissions {
  entityMappings: SectionPermissions;
  fieldMappings: SectionPermissions;
  filters: SectionPermissions;
  scheduler: SectionPermissions;
  reports: SectionPermissions;
  royaltyStatements?: SectionPermissions;
  userManagement?: SectionPermissions;
  contextGraph: SectionPermissions;
}

/**
 * User role record
 */
export interface UserRoleEntity extends ITableEntity {
  userId: string;              // Azure AD user ID (also used as rowKey)
  email: string;               // User's email address
  displayName?: string;        // User's display name from Azure AD
  role: UserRole;              // Primary role assigned to user
  customPermissions?: string[]; // Override specific permissions (JSON array)
  isActive: boolean;           // Whether user is active
  createdAt: string;           // ISO string timestamp of creation
  updatedAt: string;           // ISO string timestamp of last update
  createdBy: string;           // User ID who created this record
  lastLoginAt?: string;        // ISO string timestamp of last login
}

/**
 * Role permission definitions
 */
export interface RolePermissionEntity extends ITableEntity {
  role: UserRole;              // Role name (also used as rowKey)
  permissions: UserPermissions; // Complete permission structure
  isPublic: boolean;           // Whether this role allows public access
  description?: string;        // Description of the role
  createdAt: string;           // ISO string timestamp of creation
  updatedAt: string;           // ISO string timestamp of last update
}

/**
 * User context interface for authenticated requests
 */
export interface AuthenticatedUser {
  userId: string;              // Database user ID (for foreign key references)
  objectId?: string;           // Azure AD Object ID
  email: string;               // User's email address
  displayName?: string;        // User's display name
  role: UserRole;              // User's assigned role
  roleNames?: string[];        // Optional: all role names assigned in RBAC
  permissions: UserPermissions; // Resolved permissions
  isActive: boolean;           // Whether user is active
}

/**
 * Request context with authenticated user information
 */
export interface AuthenticatedRequest {
  user: AuthenticatedUser;
  // Other request properties can be added here
}

/**
 * Normalized RBAC entities for Azure SQL (not yet wired into runtime).
 */
export interface RbacUser {
  userId: string;
  objectId?: string | null;
  email: string;
  displayName?: string | null;
  tenantId?: string | null;
  source: 'entra' | 'manual' | 'service';
  status: 'active' | 'disabled';
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
}

export interface RbacRole {
  roleId: string;
  name: string;
  description?: string | null;
  category?: string | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RbacRoleWithPermissions extends RbacRole {
  permissions: string[];
}

export interface RbacPermission {
  permissionId: string;
  key: string;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RbacUserRole {
  userId: string;
  roleId: string;
  assignedBy?: string | null;
  assignedAt: string;
}

export interface RbacRolePermission {
  roleId: string;
  permissionId: string;
  grantedAt: string;
}

export interface RbacLoginAudit {
  auditId: string;
  userId?: string | null;
  objectId?: string | null;
  email?: string | null;
  tenantId?: string | null;
  isSuccess: boolean;
  failureReason?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  correlationId: string;
  createdAt: string;
}

export interface RbacUserWithRoles extends RbacUser {
  roles: RbacRole[];
  permissions: string[]; // flattened permission keys
}

export interface UserApiKey {
  apiKeyId: string;
  userId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
  createdBy?: string | null;
  revokedAt?: string | null;
  revokedBy?: string | null;
}

export interface ContextGraphBuild {
  buildId: string;
  scope: string;
  corpusId: string;
  extractorVersion: string;
  sourceCommit?: string | null;
  generatedAt: string;
  publishedAt: string;
  publishedBy?: string | null;
  status: string;
  isCurrent: boolean;
  rawMetadataJson?: string | null;
}

export interface ContextGraphNode {
  nodeId: string;
  type: string;
  label: string;
  summary?: string | null;
  sourcePath?: string | null;
  sourceLine?: number | null;
  tags: string[];
  rawJson: any;
}

export interface ContextGraphEdge {
  edgeId: string;
  fromNodeId: string;
  toNodeId: string;
  relation: string;
  confidence: string;
  confidenceScore: number;
  rawJson: any;
}

export interface ContextGraphEvidence {
  evidenceId: string;
  edgeId: string;
  path: string;
  line?: number | null;
  excerpt?: string | null;
}
